define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../System/Vec3Obj'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    Vec3Obj
) {
    'use strict'

    /**
     * 相机视点，用于视点获取及定位。
     * 
     * @demo {@link http://lbs.zjkelan.com/Camera.html|CooWasm Camera demo}
     * @alias Camera
     * @constructor
     *
     * @param {Object} [props] 包含如下属性的Object：
     * @param {Boolean} [props.bGet=true] 是否获取视点信息，为fasle时则为视点信息设置。
     * @param {Vec3Obj} props.focalPoint 目标点，当bGet=false才需要传入。
     * @param {Number} [props.azimuth=0.0] 视点高度角。
     * @param {Number} [props.pitch=-45.0] 视点旋转角。
     * @param {Number} [props.distance=0.0] 观察点距离目标点距离，单位：米。
     * @param {Number} [props.duration=5.0] 定位飞行时间，单位：秒。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @example
     * //获取视点
     * camera = new CooWasm.Camera({
     *        bGet: true
     *     }, state => {
     * })
     */
    class Camera {
        /**
         * @description 是否获取视点信息。
         * @type {Boolean}
         */
        bGet
        /**
         * @description 目标点。
         * @type {Vec3Obj}
         */
        focalPoint
        /**
         * @description 视点视线上方向。
         * @type {Number}
         */
        azimuth
        /**
         * @description 视点旋转角。
         * @type {Number}
         */
        pitch
        /**
         * @description 观察点距离目标点距离，单位：米。
         * @type {Number}
         */
        distance
        /**
         * @description 定位飞行时间，单位：秒。
         * @type {Number}
         */
        duration
        /**
         * @description Camera在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "Camera"
        static avatarType = "deSMP.Avatar.EmptyAvatar"
        static componentType = null
        constructor(props, callback) {
            if (!defined(props)) {
                props = {}
            }
            this.bGet = defaultValue(props.bGet, true)
            var aCategory = encode("deSMP.Avatar")
            var aName = encode("EmptyAvatar")
            var cCategory = encode("")
            var cName = encode("")
            var aProps = ""
            var cProps = ""
            if (defined(props.bLocate)) {
                aProps += "LocateType:1|"
            }
            aProps += this.toStr("focalPoint", props.focalPoint, "LocationFocalPoint")
            aProps += this.toStr("azimuth", props.azimuth, "LocationAzimuth")
            aProps += this.toStr("pitch", props.pitch, "LocationPitch")
            aProps += this.toStr("distance", props.distance, "LocationDistance")
            aProps += this.toStr("duration", props.duration, "LocationDuration")
            
            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndComponent(encode(""), aCategory, aName, encode(aProps), cCategory, cName, encode(cProps), encode(""), encode(""), true, true)
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentUuid = idArray[1]
                if (this.bGet) {
                    callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                        Element.prototype.NativeAPI.SetViewLocation(encode(this.avatarUuid))
                        this.getProperties()
                        if (defined(callback)) {
                            callback(state)
                        }
                        Element.prototype.Map.cameras.push(this)
                    })
                }else{
                    if (!defined(props.focalPoint)) {
                        console.log("need focalPoint!")
                        return
                    }
                    callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                        Element.prototype.Map.cameras.push(this)
                        if (defined(props.bLocate)) {
                            callbackSystem.register("", "LocateAvatar", (state) => {
                                if (defined(callback)) {
                                    callback(state)
                                }
                            })
                            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
                        } else {
                            if (defined(callback)) {
                                callback(state)
                            }
                        }
                    })
                }
            }
        }
        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        /**
         * 根据视点信息，创建一个新的 Camera 对象。
         *
         * @param {Vec3Obj} focalPoint 目标点。
         * @param {Number} azimuth 高度角。
         * @param {Number} pitch 俯仰角。
         * @param {Number} distance 观察点距离目标点距离，单位：米。
         * @param {Number} duration 定位飞行时间，单位：秒。
         * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
         * @returns {CameraObj} 新的CameraObj实例。
         * @static
         * 
         * @example
         * let camera = CooWasm.Camera.locateFrom(CooWasm.Vec3Obj.fromStr("113.6940831517 30.9937961181 -2514.6437108647"), 350.5359192, -88.2742691, 9400031, 1,()=>{
         *      console.log("locate over")
         *  })
         * 
         */
        static locateFrom(focalPoint, azimuth, pitch, distance, duration, callback) {
            let camera = new Camera({
                bLocate: true,
                bGet: false,
                focalPoint: focalPoint,
                azimuth: azimuth,
                pitch: pitch,
                distance: distance,
                duration: duration
            }, callback)
            return camera
        }
        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} props 包含如下属性的Object：
         * @param {Vec3Obj} [props.focalPoint] 目标点。
         * @param {Number} [props.azimuth] 视点高度角。
         * @param {Number} [props.pitch] 视点旋转角。
         * @param {Vec3Obj} [props.distance] 观察点距离目标点距离，单位：米。
         * @param {Vec3Obj} [props.duration] 定位飞行时间，单位：秒。
         * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
         * @example
         * camera.update({
         *    duration:1
         * })
         */
        update(props, callback) {
            let avatarProps = ""
            let componentProps = ""
            if (defined(props)) {
                if (defined(props.focalPoint)) {
                    this.focalPoint = props.focalPoint
                    avatarProps += "LocationFocalPoint:" + this.focalPoint.toStr() + "|"
                }
                if (defined(props.azimuth)) {
                    this.azimuth = props.azimuth
                    avatarProps += "LocationAzimuth:" + this.azimuth + "|"
                }
                if (defined(props.pitch)) {
                    this.pitch = props.pitch
                    avatarProps += "LocationPitch:" + this.pitch + "|"
                }
                if (defined(props.distance)) {
                    this.distance = props.distance
                    avatarProps += "LocationDistance:" + this.distance + "|"
                }
                if (defined(props.duration)) {
                    this.duration = props.duration
                    avatarProps += "LocationDuration:" + this.duration + "|"
                }
                
                callbackSystem.register("UpdateAvatarAndComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                })
                Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentUuid), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }
        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        /**
         * 定位视点。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * camera.locate(state=>{console.log("locate status:" + state)})
         */
        locate(callback) {
            let avatarProps = "LocateType:1|"
            let componentProps = ""
            callbackSystem.register("UpdateAvatarAndComponent", this.avatarUuid, (state) => {
                callbackSystem.register("", "LocateAvatar", (state1) => {
                    if (defined(callback)) {
                        callback(state1)
                    }
                })
                Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
            })
            Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentUuid), encode(avatarProps), encode(componentProps), true)
        }

        /**
         * 获取视点当前属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * camera.getProperties()
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "LocateType") {

                } else if (name == "LocationFocalPoint") {
                    this.focalPoint = Vec3Obj.fromStr(value)
                } else if (name == "LocationAzimuth") {
                    this.azimuth = Number(value)
                } else if (name == "LocationPitch") {
                    this.pitch = Number(value)
                } else if (name == "LocationDistance") {
                    this.distance = Number(value)
                } else if (name == "LocationDuration") {
                    this.duration = Number(value)
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁图层后触发的函数回调。
         * @returns null 空值。
         * @example
         * camera = camera.destroy()
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.cameras = Element.prototype.Map.cameras.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return Camera
})